import { useRef, useEffect, useMemo } from "react";
import { useGetTickerQuery } from "../../RTK/Slices/Slices/apiSliceMeat";

/**
 * هوك بسيط يرجع true لو الشريط المتحرك ظاهر حالياً (فيه رسائل)،
 * تقدر تستخدمه في أي مكون تاني (زي الهيدر) عشان يظبط مكانه تبعًا لظهور الشريط.
 * بيستخدم نفس الـ cache بتاع useGetTickerQuery فمفيش طلب شبكة إضافي.
 */
export const useHasTicker = () => {
    const { data } = useGetTickerQuery();
    return (data?.data ?? []).length > 0;
};

const TickerBar = () => {
    const { data, isLoading, isError } = useGetTickerQuery();
    const messages = useMemo(() => data?.data ?? [], [data]);
    const trackRef   = useRef(null);
    const containerRef = useRef(null);
    const animRef    = useRef(null);
    const posRef     = useRef(null);      // موضع X الحالي

    /* دمج كل الرسائل في نص واحد بفاصل  •  */
    const fullText = messages.map(m => m.message).join("   •   ");
    /* متوسط السرعة من الرسائل */
    const speed = messages.length
        ? messages.reduce((s, m) => s + (m.speed || 40), 0) / messages.length
        : 40;
    /* ألوان من أول رسالة */
    const bgColor   = messages[0]?.bg_color ?? "#c0392b";
    const textColor = messages[0]?.color    ?? "#ffffff";

    /* ── تحريك بـ requestAnimationFrame ── */
    useEffect(() => {
        if (!trackRef.current || !containerRef.current || messages.length === 0) return;

        const track     = trackRef.current;
        const container = containerRef.current;

        // ابدأ من يسار الحاوية (خارج الشاشة) عشان يتحرك من الشمال لليمين
        if (posRef.current === null) {
            posRef.current = -track.offsetWidth;
        }

        const pxPerSec = speed * 1.5;   // تحويل الوحدة

        let lastTime: number | null = null;

        const step = (now: number) => {
            if (lastTime === null) lastTime = now;
            const dt = (now - lastTime) / 1000;   // ثواني
            lastTime = now;

            posRef.current += pxPerSec * dt;

            // لو خرج من اليمين كليًا → أعد من الشمال
            if (posRef.current > container.offsetWidth) {
                posRef.current = -track.offsetWidth;
            }

            track.style.transform = `translateX(${posRef.current}px)`;
            animRef.current = requestAnimationFrame(step);
        };

        animRef.current = requestAnimationFrame(step);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, [messages, speed]);

    if (isLoading || isError || messages.length === 0) return null;

    return (
        <div
            ref={containerRef}
            dir="ltr"
            style={{
                background: bgColor,
                overflow: "hidden",
                whiteSpace: "nowrap",
                height: 42,
                display: "flex",
                alignItems: "center",
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                zIndex: 60,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
        >
            {/* النص المتحرك */}
            <span
                ref={trackRef}
                style={{
                    display: "inline-block",
                    color: textColor,
                    fontFamily: "Cairo, sans-serif",
                    fontSize: 15,
                    fontWeight: 600,
                    willChange: "transform",
                    paddingLeft: 40,
                    userSelect: "none",
                }}
            >
                {fullText}
            </span>
        </div>
    );
};

export default TickerBar;
